import React, { useCallback, useMemo, useRef, useState } from 'react';
import { chartElementAtEvent } from './chartInteraction';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { Chart as ChartJS } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { formatAxisCompactK } from './chartFormatters';
import type { KpiDrilldownSpec, KpiHeatmapCustomerApiData } from './kpiDashboardApi';
import { KPI_PRIORITY_LABELS, type KpiPriorityLabel } from './types';
import { SectionLabel } from './KpiDashboardContent';
import KpiChartPanel from './charts/KpiChartPanel';
import KpiCustomerHeatmapTable from './KpiCustomerHeatmapTable';

const INCIDENT_LINE = '#3498DB';
const REQUEST_LINE = '#002366';
const P3_BAR = '#D4AF37';
const P4_BAR = '#7D52B3';
const P_OTHER = '#e2e8f0';

function isKpiPriorityLabel(s: string): s is KpiPriorityLabel {
  return (KPI_PRIORITY_LABELS as readonly string[]).includes(s);
}

function priorityBarColorsForLabels(labels: readonly string[]): string[] {
  return labels.map((label) => {
    if (label === 'Moderate') return P3_BAR;
    if (label === 'Low') return P4_BAR;
    return P_OTHER;
  });
}

export interface KpiHeatmapCustomerTabProps {
  data: KpiHeatmapCustomerApiData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  onRetry?: () => void;
  /** KPI dashboard Priority filter — when not All, the backlog chart shows that priority only. */
  priorityFilter?: string;
  /** Opens the same KPI drill-down ticket table as the main dashboard. */
  onDrill: (spec: KpiDrilldownSpec) => void;
}

const KpiHeatmapCustomerTab: React.FC<KpiHeatmapCustomerTabProps> = ({
  data,
  isLoading,
  isFetching,
  error,
  onRetry,
  priorityFilter = 'All',
  onDrill,
}) => {
  const [customerSortDesc, setCustomerSortDesc] = useState(false);
  const priorityBarRef = useRef<ChartJS<'bar'>>(null);
  const volumeLineRef = useRef<ChartJS<'line'>>(null);

  const priorityBarLabels = useMemo((): KpiPriorityLabel[] => {
    if (priorityFilter === 'All' || !priorityFilter) return [...KPI_PRIORITY_LABELS];
    if (isKpiPriorityLabel(priorityFilter)) return [priorityFilter];
    return [...KPI_PRIORITY_LABELS];
  }, [priorityFilter]);

  const priValues = useMemo(() => {
    if (!data?.backlogPriority) return [];
    return priorityBarLabels.map((l) => data.backlogPriority[l] ?? 0);
  }, [data, priorityBarLabels]);

  const handlePriorityBarClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const chart = priorityBarRef.current;
      if (!chart) return;
      const els = chartElementAtEvent(chart, event);
      const el = els[0];
      if (el == null) return;
      const idx = el.index;
      const count = priValues[idx] ?? 0;
      if (count <= 0) return;
      const label = priorityBarLabels[idx];
      if (!label) return;
      onDrill({ kind: 'heatmap_priority', priority: label });
    },
    [onDrill, priValues, priorityBarLabels]
  );

  const handleVolumeLineClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const chart = volumeLineRef.current;
      if (!chart) return;
      const volumeByWeek = data?.volumeByWeek;
      if (!volumeByWeek?.length) return;
      const native = event.nativeEvent;
      const elements = chart.getElementsAtEventForMode(native, 'nearest', { intersect: true }, false);
      if (!elements.length) return;
      const { datasetIndex, index } = elements[0];
      const week = volumeByWeek[index];
      if (!week) return;
      const count = datasetIndex === 0 ? week.incident : week.request;
      if (count <= 0) return;
      onDrill({
        kind: 'heatmap_volume_week',
        weekStart: week.weekStart,
        series: datasetIndex === 0 ? 'INCIDENT' : 'SERVICE_REQUEST',
      });
    },
    [data?.volumeByWeek, onDrill]
  );

  const sortedRows = useMemo(() => {
    if (!data?.heatmap.rows.length) return [];
    const rows = [...data.heatmap.rows];
    rows.sort((a, b) => {
      const cmp = a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' });
      return customerSortDesc ? -cmp : cmp;
    });
    return rows;
  }, [data?.heatmap.rows, customerSortDesc]);

  if (isLoading && !data) {
    return (
      <div className="space-y-8" aria-busy="true" aria-label="Loading heatmap">
        <div>
          <div className="mb-3 h-3 w-48 animate-pulse rounded bg-slate-200" />
          <div className="h-64 animate-pulse rounded-xl border border-slate-200/80 bg-gradient-to-br from-slate-100 to-slate-50 shadow-sm" />
        </div>
        <div>
          <div className="mb-3 h-3 w-56 animate-pulse rounded bg-slate-200" />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="h-72 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
            <div className="h-72 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
          </div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-xl border border-red-200/80 bg-white p-8 text-center shadow-sm">
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-500" aria-hidden />
        <p className="mt-3 text-sm font-semibold text-red-900">Could not load heatmap data</p>
        <p className="mt-1 text-sm text-red-700/90">{error.message}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!data) return null;

  const { volumeByWeek } = data;

  const lineLabels = volumeByWeek.map((w) => w.weekLabel);
  const incidentData = volumeByWeek.map((w) => w.incident);
  const requestData = volumeByWeek.map((w) => w.request);
  const maxVol = Math.max(0, ...incidentData, ...requestData, 1);

  return (
    <div className="relative">
      {isFetching && (
        <div
          className="pointer-events-none absolute right-0 top-0 z-10 flex items-center gap-1.5 rounded-full border border-indigo-200/80 bg-white/95 px-3 py-1.5 text-xs font-medium text-indigo-800 shadow-md shadow-indigo-900/10 backdrop-blur-sm"
          aria-live="polite"
        >
          <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-indigo-600" aria-hidden />
          Updating
        </div>
      )}
      {error && data && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
          <span className="min-w-0 flex-1">
            Showing cached data. Latest refresh failed: {error.message}
          </span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="shrink-0 font-semibold text-amber-900 underline decoration-amber-600/50 underline-offset-2 hover:text-amber-950"
            >
              Retry
            </button>
          )}
        </div>
      )}

      <section className="mb-8">
        <SectionLabel>Customer backlog</SectionLabel>
        <KpiCustomerHeatmapTable
          title="Tickets Backlog By Customer"
          variant="slate"
          heatmap={data.heatmap}
          sortedRows={sortedRows}
          onToggleCustomerSort={() => setCustomerSortDesc((d) => !d)}
          maxCount={data.heatmap.maxCount > 0 ? data.heatmap.maxCount : 1}
          onCellDrill={onDrill}
        />
      </section>

      <section>
        <SectionLabel>Volume &amp; backlog by priority</SectionLabel>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
          <KpiChartPanel title="Tickets Volume By Week" className="lg:col-span-1" flexFill>
            <div className="relative min-h-[280px] w-full flex-1 px-1">
            <Line
              ref={volumeLineRef}
              onClick={handleVolumeLineClick}
              data={{
                labels: lineLabels,
                datasets: [
                  {
                    label: 'Incident',
                    data: incidentData,
                    borderColor: INCIDENT_LINE,
                    backgroundColor: 'rgba(52, 152, 219, 0.12)',
                    fill: true,
                    tension: 0.35,
                    pointRadius: 4,
                    pointHoverRadius: 7,
                    pointBackgroundColor: '#fff',
                    pointBorderColor: INCIDENT_LINE,
                    pointBorderWidth: 2,
                    borderWidth: 2,
                  },
                  {
                    label: 'Request',
                    data: requestData,
                    borderColor: REQUEST_LINE,
                    backgroundColor: 'rgba(0, 35, 102, 0.08)',
                    fill: true,
                    tension: 0.35,
                    pointRadius: 4,
                    pointHoverRadius: 7,
                    pointBackgroundColor: '#fff',
                    pointBorderColor: REQUEST_LINE,
                    pointBorderWidth: 2,
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { top: 8, right: 8, bottom: 4 } },
                interaction: { mode: 'nearest', intersect: true },
                onHover: (event, elements, chart) => {
                  const canvas = chart.canvas;
                  if (!elements.length) {
                    canvas.style.cursor = 'default';
                    return;
                  }
                  const el = elements[0];
                  const week = volumeByWeek[el.index];
                  if (!week) {
                    canvas.style.cursor = 'default';
                    return;
                  }
                  const count = el.datasetIndex === 0 ? week.incident : week.request;
                  canvas.style.cursor = count > 0 ? 'pointer' : 'default';
                },
                plugins: {
                  legend: {
                    position: 'top',
                    align: 'end',
                    labels: {
                      boxWidth: 10,
                      boxHeight: 10,
                      padding: 16,
                      usePointStyle: true,
                      color: '#475569',
                      font: { size: 12, weight: 500 },
                    },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    ticks: {
                      color: '#64748b',
                      maxRotation: 90,
                      minRotation: 90,
                      autoSkip: true,
                      maxTicksLimit: 24,
                    },
                  },
                  y: {
                    beginAtZero: true,
                    suggestedMax: maxVol * 1.1,
                    grid: { color: 'rgba(148, 163, 184, 0.25)' },
                    border: { display: false },
                    ticks: {
                      color: '#94a3b8',
                      callback: (v) => formatAxisCompactK(v as number),
                    },
                  },
                },
              }}
            />
            </div>
          </KpiChartPanel>

          <KpiChartPanel title="Ticket Backlog by Priority" className="lg:col-span-1" flexFill>
            <div className="relative min-h-[280px] w-full flex-1 px-1">
            <Bar
              ref={priorityBarRef}
              onClick={handlePriorityBarClick}
              data={{
                labels: priorityBarLabels,
                datasets: [
                  {
                    label: 'Backlog',
                    data: priValues,
                    backgroundColor: priorityBarColorsForLabels(priorityBarLabels),
                    borderRadius: 6,
                    barThickness: 36,
                    datalabels: {
                      display: true,
                      anchor: 'end',
                      align: 'end',
                      offset: 4,
                      color: '#334155',
                      font: { weight: 600, size: 11 },
                      formatter: (v: number) => String(v ?? 0),
                    },
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { top: 20 } },
                onHover: (event, elements) => {
                  const canvas = event.native?.target;
                  if (canvas instanceof HTMLElement) {
                    const el = elements[0];
                    const idx = el?.index;
                    const canClick =
                      idx !== undefined && (priValues[idx] ?? 0) > 0;
                    canvas.style.cursor = canClick ? 'pointer' : 'default';
                  }
                },
                plugins: {
                  legend: { display: false },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    ticks: { color: '#64748b', font: { size: 12, weight: 600 } },
                  },
                  y: {
                    beginAtZero: true,
                    grid: { color: 'rgba(148, 163, 184, 0.25)' },
                    border: { display: false },
                    ticks: {
                      color: '#94a3b8',
                      callback: (v) => formatAxisCompactK(v as number),
                    },
                  },
                },
              }}
            />
            </div>
          </KpiChartPanel>
        </div>
      </section>
    </div>
  );
};

export default KpiHeatmapCustomerTab;
